import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_RowVirtualizer,
  MRT_RowData,
  MRT_TableOptions,
} from "mantine-react-table"
import { useDebouncedCallback } from "@mantine/hooks"
import { Input, Output } from "../../../../@client/api"
import { useTranslation } from "react-i18next"
import { useMrtLocalization } from "../use-mrt-localization"
import { useInfiniteGet } from "../../../../typed-client/hooks/use-infinite-get"
import { PaginatedListRequest } from "../infinit-scrolling-list"
import { ExclusiveProperties } from "../../../../utils/exclusive-properties"
import { PaginatedData } from "../../../../@client/Theasoft_ApiServer__Utils_Pagination"
import { useQueryClient } from "@tanstack/react-query"
import { useIsSmallScreen } from "../../../../utils/useIsSmallScreen"

const pageSize = 100

type KeysOfType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never
}[keyof T]

type VirtualTableUrls = KeysOfType<Output, PaginatedData<unknown>>

interface Props<URL extends VirtualTableUrls, TData extends MRT_RowData> {
  url: URL
  partialInput: ExclusiveProperties<Input[URL], PaginatedListRequest>
  onClickRow?: (row: TData) => void
  tableOptions: Omit<MRT_TableOptions<TData>, "data">
}

export function InfinitScrollingTable<
  URL extends VirtualTableUrls,
  TData extends MRT_RowData = Output[URL]["data"],
>({ url, partialInput, onClickRow, tableOptions }: Props<URL, TData>) {
  const { t } = useTranslation("app_server")

  const tableContainerRef = React.useRef<HTMLDivElement>(null)
  const rowVirtualizerInstanceRef =
    React.useRef<MRT_RowVirtualizer<HTMLDivElement, HTMLTableRowElement>>(null)

  const { data, fetchNextPage, isFetching, isLoading, isError, refetch } =
    useInfiniteGet(
      url,
      (params) =>
        ({
          firstItem: params.after ?? 0,
          pageSize: pageSize,
          ...partialInput,
        }) as Input[URL],
      (lastPage) => ({
        after: lastPage.firstItem + pageSize,
        before: lastPage.firstItem,
        take: pageSize,
      })
    )

  const flatData: TData[] = React.useMemo(
    () => data?.pages.flatMap((page) => page.data as unknown as TData) ?? [],
    [data, isFetching]
  )

  const totalDBRowCount = data?.pages?.[0]?.totalRows ?? 0
  const totalFetched = flatData.length

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement
        if (
          scrollHeight - scrollTop - clientHeight < 400 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  )

  const queryClient = useQueryClient()

  const debouncedRefetch = useDebouncedCallback(() => {
    flatData.length > 0 && rowVirtualizerInstanceRef.current?.scrollToIndex(0)
    queryClient.removeQueries({ queryKey: [url] })
    refetch()
  }, 500)

  React.useEffect(debouncedRefetch, [
    partialInput,
    tableOptions.state?.columnFilters,
    tableOptions.state?.globalFilter,
    tableOptions.state?.sorting,
  ])

  React.useEffect(() => {
    tableContainerRef.current &&
      fetchMoreOnBottomReached(tableContainerRef.current)
  }, [fetchMoreOnBottomReached])

  const { locale } = useMrtLocalization()

  const isSmallScreen = useIsSmallScreen()

  const table = useMantineReactTable({
    enableStickyHeader: true,
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => onClickRow && onClickRow(row.original),
      style: { cursor: onClickRow ? "pointer" : undefined },
    }),
    ...tableOptions,
    data: flatData,
    enablePagination: false,
    enableRowVirtualization: true,
    mantineTableContainerProps: {
      ref: tableContainerRef,
      style: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
      onScroll: (event: React.UIEvent<HTMLDivElement>) =>
        fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    mantinePaperProps: {
      style: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    enableBottomToolbar: !isSmallScreen,
    mantineBottomToolbarProps: () => ({
      fz: "sm",
      style: { alignContent: "center" },
    }),
    renderBottomToolbarCustomActions: () =>
      t("VirtualTable_Footer", {
        count: totalFetched,
        total: totalDBRowCount,
      }),
    mantineTableBodyCellProps: () => ({
      fw: 400,
      style: { textRendering: "optimizeLegibility" },
    }),
    state: {
      ...tableOptions.state,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      showLoadingOverlay: isLoading,
      density: "xs",
    },
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 20 },
    localization: locale,
  })

  return <MantineReactTable table={table} />
}
